<template>
  <div class="available-dates">
    <h2 class="section-title">
      <Badge :count="questionIndex" /> {{ $t('appointment_form_page.available_dates.title') }}
    </h2>
    <div v-if="!isConciergeEventContext">
      <p v-if="showCallUsSection" class="call-us-section">
        <span
          class="pe-3"
          v-html="$t('appointment_form_page.available_dates.direct_appointment')"
        />
        <a
          class="text-decoration-underline"
          :aria-label="$t('appointment_form_page.available_dates.direct_appointment')"
          v-html="$t('appointment_form_page.available_dates.direct_appointment_cta')"
          :href="phoneLink"
        />
      </p>
      <div ref="wrapper">
        <LoadingComponent v-show="loadingDates" :style="loadingMinHeight" />
        <div :key="componentKey">
          <VueDatePicker
            v-show="!loadingDates"
            ref="datePickerRef"
            style="place-content: center"
            :month-change-on-scroll="false"
            :disabled-dates="disabledDatesString"
            :start-date="startDate"
            :allowed-dates="availableDatesKeys"
            focus-start-date
            inline
            auto-apply
            prevent-min-max-navigation
            :min-date="minDate"
            :max-date="maxDate"
            :year-range="[new Date().getFullYear(), new Date().getFullYear() + 1]"
            :enable-time-picker="false"
            :locale="locale"
            timezone="GMT"
            @update-month-year="updateDateCalendar"
            @date-update="selectDate"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <LoadingComponent v-show="loadingDates" :style="loadingMinHeight" />
      <div v-if="showRegularCalendar" :key="componentKey" ref="wrapper">
        <VueDatePicker
          v-show="!loadingDates"
          ref="datePickerRef"
          style="place-content: center"
          :month-change-on-scroll="false"
          :disabled-dates="disabledDatesString"
          :start-date="startDate"
          :allowed-dates="availableDatesKeys"
          :disabled="disabled"
          focus-start-date
          inline
          auto-apply
          prevent-min-max-navigation
          :min-date="minDate"
          :max-date="maxDate"
          :year-range="[new Date().getFullYear(), new Date().getFullYear() + 1]"
          :enable-time-picker="false"
          :locale="locale"
          timezone="GMT"
          @update-month-year="updateDateCalendar"
          @date-update="selectDate"
        />
      </div>
      <AvailableEventDates
        v-else
        ref="wrapper"
        :disabled="disabled"
        :available-dates="availableDatesKeys"
        :selected-date="selectedDate"
        @select-date="selectDate"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  nextTick,
  reactive,
  computed,
  defineAsyncComponent,
  useTemplateRef
} from 'vue'
import { usePage } from '@inertiajs/vue3'
import { addDays, getMonth, getYear, isBefore, startOfMonth } from 'date-fns'
import { format as tzFormat } from 'date-fns-tz'
import VueDatePicker from '@vuepic/vue-datepicker'
import { AppointmentDate, AvailabilityHoursByDateMap } from '~concierge/types/AppointmentTypes'
import '@vuepic/vue-datepicker/dist/main.css'
import LoadingComponent from '~conciergeUiCommons/components/LoadingComponent.vue'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import Badge from '~conciergeUiCommons/components/Badge.vue'
import { getActiveLanguage, I18n } from 'laravel-vue-i18n'
import { ConciergeEventData, LocalizedConciergeEvent } from '~types/concierge-event'

export default defineComponent({
  name: 'AvailableDates',
  components: {
    VueDatePicker: defineAsyncComponent(() => import('@vuepic/vue-datepicker')),
    LoadingComponent,
    Badge,
    AvailableEventDates: defineAsyncComponent(
      () => import('~conciergeEvent/components/AvailableEventDates.vue')
    )
  },
  emits: ['selectDate', 'monthUpdate', 'child-mounted'],
  props: {
    availableDates: {
      type: Map as PropType<AvailabilityHoursByDateMap>,
      required: true
    },
    disabledDates: {
      type: Array as PropType<AppointmentDate[]>,
      required: true
    },
    startDate: {
      type: Date,
      required: true
    },
    componentKey: {
      type: Number,
      required: true
    },
    loadingDates: {
      type: Boolean,
      default: true
    },
    questionIndex: {
      type: Number,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    selectedDate: {
      type: String as PropType<AppointmentDate | null>,
      default: null
    }
  },
  setup(__, { expose }) {
    const page = usePage()
    const timezone = ref<string>((page.props.timezone as string | undefined) || 'America/Montreal')
    const wrapper = useTemplateRef('wrapper')
    const datePickerRef = useTemplateRef<InstanceType<typeof VueDatePicker>>('datePickerRef')
    const identityStore = useIdentityStore()
    const isConciergeEventContext = computed((): boolean => {
      return identityStore.service === 'conciergeEvent'
    })
    const event = computed((): ConciergeEventData | null => {
      return identityStore.event || null
    })
    const showRegularCalendar = ref<boolean>(event.value?.showRegularCalendar ?? false)
    const loaderHeight = ref<number>(290)
    async function scrollToViewport() {
      await nextTick()
      await new Promise(resolve => setTimeout(resolve, 500))
      await nextTick(() => {
        wrapper.value?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest'
        })
      })
    }
    expose({ scrollToViewport })
    const currentMonth = new Date(getYear(new Date()), getMonth(new Date()))
    let minStoredDate = ref(
      tzFormat(startOfMonth(currentMonth), 'yyyy-MM-dd', { timeZone: timezone.value })
    )
    let selectedLocale = ref<string>(getActiveLanguage())
    const currentLocaleInstance = reactive<I18n>(I18n.getSharedInstance())

    return {
      timezone,
      datePickerRef,
      event,
      showRegularCalendar,
      isConciergeEventContext,
      selectedLocale,
      currentLocaleInstance,
      minStoredDate,
      loaderHeight,
      wrapper,
      scrollToViewport,
      identityStore
    }
  },
  watch: {
    currentLocaleInstance: {
      handler(localeInstance) {
        this.selectedLocale = localeInstance.options.lang
      },
      deep: true
    }
  },
  computed: {
    locale(): string {
      const locales = new Map([
        ['fr', 'fr-CA'],
        ['en', 'en-CA']
      ])
      return locales.get(this.selectedLocale) ?? 'fr-CA'
    },
    loadingMinHeight(): string {
      return `min-height: ${this.loaderHeight}px`
    },
    availableDatesKeys(): AppointmentDate[] {
      if (this.loadingDates) {
        return []
      }
      return Array.from(this.availableDates.keys())
    },
    minDate(): string {
      if (this.isConciergeEventContext && this.eventStartDate) {
        return this.eventStartDate
      }
      let stringifyMinDate = tzFormat(new Date(), 'yyyy-MM-dd', { timeZone: this.timezone })

      if (this.availableDates.size > 0 && !this.availableDates.has(this.minStoredDate)) {
        const iterator = this.availableDates.keys()
        stringifyMinDate = iterator.next()?.value ?? stringifyMinDate
      }

      if (!isBefore(this.minStoredDate, stringifyMinDate)) {
        this.minStoredDate = tzFormat(stringifyMinDate, 'yyyy-MM-dd', {
          timeZone: this.timezone
        })
      }

      return this.minStoredDate
    },
    localizedEvent(): LocalizedConciergeEvent | null {
      if (!this.event) {
        return null
      }
      const locale = this.selectedLocale as keyof ConciergeEventData
      return this.event[locale] ?? null
    },
    eventEndDate(): string | null {
      if (this.isConciergeEventContext && this.localizedEvent) {
        return this.localizedEvent.endDate ?? null
      }
      return null
    },
    eventStartDate(): string | null {
      if (this.isConciergeEventContext && this.localizedEvent) {
        return this.localizedEvent.startDate ?? null
      }
      return null
    },
    maxDate(): string {
      if (this.isConciergeEventContext && this.eventEndDate) {
        return this.eventEndDate
      }
      let days = 90
      if (this.identityStore.identity?.settings?.maxBookingDays) {
        days = this.identityStore.identity?.settings?.maxBookingDays
      }
      return tzFormat(addDays(new Date(), days), 'yyyy-MM-dd', { timeZone: this.timezone })
    },
    disabledDatesString(): AppointmentDate[] {
      if (this.loadingDates) {
        return []
      }
      return this.disabledDates
    },
    phoneLink(): string {
      if (this.identityStore.identity?.contact.phone) {
        return `tel:${this.identityStore.identity?.contact.phone}`
      }
      return ''
    },
    showCallUsSection(): boolean {
      return !this.identityStore.identity?.settings?.allowSameDayBooking
    }
  },
  methods: {
    selectDate(date: Date) {
      if (date instanceof Date) {
        const parsedDateToString =
          date.getFullYear() +
          '-' +
          (date.getMonth() + 1).toLocaleString('en-US', {
            minimumIntegerDigits: 2,
            useGrouping: false
          }) +
          '-' +
          date.getDate().toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })

        this.$emit('selectDate', parsedDateToString)
      } else {
        console.warn(date + ' is not and instance of Date')
      }
    },
    updateDateCalendar(payload: Record<string, number>) {
      if (!this.loadingDates && payload.month != this.startDate.getMonth()) {
        this.$emit('monthUpdate', { month: payload.month, year: payload.year })
      }
    }
  }
})
</script>
<style lang="scss">
@use '~conciergeUiCommons/styles/font_stacks' as *;
.dp__theme_light {
  > div:last-child {
    flex: 1 1 auto;
  }

  --dp-background-color: transparent;
  --dp-text-color: #212121;
  --dp-hover-color: var(--date-picker-hover-bg-color, var(--dealer-primary-color));
  --dp-hover-text-color: var(--date-picker-hover-text-color, #f3f3f3);
  --dp-hover-icon-color: #959595;
  --dp-primary-color: var(--date-picker-primary-bg-color, var(--dealer-primary-color)) !important;
  --dp-primary-text-color: var(--date-picker-primary-text-color, #f8f5f5);
  --dp-secondary-color: #c0c4cc;
  --dp-border-color: #ddd;
  --dp-menu-border-color: #ddd;
  --dp-border-color-hover: #aaaeb7;
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: var(--dealer-success-color);
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: var(--dealer-error-color);
  --dp-highlight-color: rgba(25, 118, 210, 0.1);

  .dp__date_hover_end:hover,
  .dp__date_hover_start:hover,
  .dp__date_hover:hover {
    opacity: var(--date-picker-hover-opacity, 0.9);
  }

  .dp__range_end:hover,
  .dp__range_start:hover,
  .dp__active_date:hover {
    background: var(--date-picker-selected-hover-bg-color, var(--dealer-secondary-color));
    color: var(--date-picker-selected-hover-text-color, var(--dealer-secondary-text-color));
    opacity: var(--date-picker-selected-hover-opacity, 1);
  }

  .dp__inner_nav {
    transition: color 0.1s ease-in-out;

    &:hover {
      color: var(--date-picker-primary-text-color, #f8f5f5);
    }
  }

  .dp__month_year_wrap {
    place-items: center;
  }

  .dp__overlay {
    --dp-background-color: var(--vff-bg-color, #f9f6fb);
    --dp-icon-color: var(--dp-text-color);

    .dp__icon {
      stroke: unset;
    }

    .dp__overlay_action:hover {
      --dp-hover-icon-color: var(--vff-bg-color, #f9f6fb);
    }
  }

  .dp__overlay_cell_disabled {
    color: var(--dp-secondary-color);

    &:hover {
      background: var(--dp-disabled-color);
    }
  }
  .dp__cell_inner {
    margin: 0 auto;
  }
}

:root {
  /*General*/
  --dp-font-family: var(--dealer-font-family, #{$dealer-font-family-default});
  --dp-border-radius: 4px;
  /*Configurable border-radius*/
  --dp-cell-border-radius: 4px;
  /*Specific border radius for the calendar cell*/
  --dp-common-transition: all 0.1s ease-in;
  /*Generic transition applied on buttons and calendar cells*/

  /*Sizing*/
  --dp-button-height: 35px;
  /*Size for buttons in overlays*/
  --dp-month-year-row-height: 35px;
  /*Height of the month-year select row*/
  --dp-month-year-row-button-size: 35px;
  /*Specific height for the next/previous buttons*/
  --dp-button-icon-height: 20px;
  /*Icon sizing in buttons*/
  --dp-cell-size: 35px;
  /*Width and height of calendar cell*/
  --dp-cell-padding: 5px;
  /*Padding in the cell*/
  --dp-common-padding: 10px;
  /*Common padding used*/
  --dp-input-icon-padding: 35px;
  /*Padding on the left side of the input if icon is present*/
  --dp-input-padding: 6px 30px 6px 12px;
  /*Padding in the input*/
  --dp-menu-min-width: 260px;
  /*Adjust the min width of the menu*/
  --dp-action-buttons-padding: 2px 5px;
  /*Adjust padding for the action buttons in action row*/
  --dp-row-maring: 5px 0;
  /*Adjust the spacing between rows in the calendar*/
  --dp-calendar-header-cell-padding: 0.5rem;
  /*Adjust padding in calendar header cells*/
  --dp-two-calendars-spacing: 10px;
  /*Space between multiple calendars*/
  --dp-overlay-col-padding: 3px;
  /*Padding in the overlay column*/
  --dp-time-inc-dec-button-size: 32px;
  /*Sizing for arrow buttons in the time picker*/
  --dp-menu-padding: 6px 8px;
  /*Menu padding*/

  /*Font sizes*/
  --dp-font-size: 1.5rem;
  /*Default font-size*/
  --dp-preview-font-size: 1.4rem;
  /*Font size of the date preview in the action row*/
  --dp-time-font-size: 1.4rem;
  /*Font size in the time picker*/

  /*Transitions*/
  --dp-animation-duration: 0.1s;
  /*Transition duration*/
  --dp-menu-appear-transition-timing: cubic-bezier(0.4, 0, 1, 1);
  /*Timing on menu appear animation*/
  --dp-transition-timing: ease-out;
  /*Timing on slide animations*/
}

.section-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 10px;
  place-items: flex-end;
}

.call-us-section {
  font-size: var(--font-size-smaller);
  color: var(--call-us-text-color, var(--dealer-primary-color));
  font-weight: 500;
  text-align: center;

  a {
    font-weight: 500;
    color: var(--call-us-link-color, var(--dealer-primary-color));
  }
}
</style>
